import type { LocationData } from './qr'

export interface MapCoordinates {
  latitude: number
  longitude: number
}

function coordinate(value: string): number | null {
  const trimmed = value.trim()
  if (!trimmed || !/^-?(?:\d+(?:\.\d+)?|\.\d+)$/.test(trimmed)) return null
  const parsed = Number(trimmed)
  return Number.isFinite(parsed) ? parsed : null
}

function coordinatePair(latitudeValue: string, longitudeValue: string): MapCoordinates | null {
  const latitude = coordinate(latitudeValue)
  const longitude = coordinate(longitudeValue)
  if (latitude === null || longitude === null) return null
  if (latitude < -90 || latitude > 90 || longitude < -180 || longitude > 180) return null
  return { latitude, longitude }
}

export function mapCoordinates(data: LocationData): MapCoordinates | null {
  const direct = coordinatePair(data.latitude, data.longitude)
  if (direct) return direct
  const queryCoordinates = /^\s*(-?(?:\d+(?:\.\d+)?|\.\d+))\s*,\s*(-?(?:\d+(?:\.\d+)?|\.\d+))\s*$/.exec(data.query)
  return queryCoordinates ? coordinatePair(queryCoordinates[1], queryCoordinates[2]) : null
}

function clamp(value: number, minimum: number, maximum: number): number {
  return Math.min(maximum, Math.max(minimum, value))
}

export function openStreetMapEmbedUrl(data: LocationData): string | null {
  const location = mapCoordinates(data)
  if (!location) return null

  const latitudeSpan = 0.006
  const longitudeSpan = Math.min(0.024, latitudeSpan / Math.max(Math.cos(location.latitude * Math.PI / 180), 0.25))
  const west = clamp(location.longitude - longitudeSpan, -180, 180)
  const east = clamp(location.longitude + longitudeSpan, -180, 180)
  const south = clamp(location.latitude - latitudeSpan, -90, 90)
  const north = clamp(location.latitude + latitudeSpan, -90, 90)
  const url = new URL('https://www.openstreetmap.org/export/embed.html')
  url.searchParams.set('bbox', [west, south, east, north].map((value) => value.toFixed(6)).join(','))
  url.searchParams.set('layer', 'mapnik')
  url.searchParams.set('marker', `${location.latitude.toFixed(6)},${location.longitude.toFixed(6)}`)
  return url.href
}
